import React, { useState, useEffect } from 'react';
import { Download, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { getInstagramTemplates, generateInstagramStory } from '../api/TemplateGenerator.api';


const TEMPLATE_CONFIG = {
  "mira_mock": { displayName: "Mirá los avances", imageTemplateModel: "mira_los_avances_de_obra_desde_tu_celular_1" },
  "avance_avance_mock": { displayName: "Avance x3", imageTemplateModel: "avance_avance_avance_1" },
  "split_banda_banca_mock": { displayName: "Split con banda", imageTemplateModel: "split_con_banda_blanca_central_1" },
  "tres_imagenes_mock": { displayName: "Tres imágenes", imageTemplateModel: "tres_imagenes_mas_logo_1" },
  "multi_mock": { displayName: "Multi imagen", imageTemplateModel: "multi_imagen_template_1" },
  "antes_despues_mock": { displayName: "Antes y Después", imageTemplateModel: "antes_despues_1" },
  "card_mock": { displayName: "Card", imageTemplateModel: "card_1" },
  "avance_lat_mock": { displayName: "Avance lateral", imageTemplateModel: "avance_lateral_grande_1" },
  "franja_mock": { displayName: "Franjas", imageTemplateModel: "franjas_destacadas_1" },
  "toma_mock": { displayName: "Toma decisión", imageTemplateModel: "toma_una_buena_decision_1" },
  "split_mock": { displayName: "Split cronológico", imageTemplateModel: "split_cronologico_1" },
  "novedades_mock": { displayName: "Visor ángulos", imageTemplateModel: "novedades_visor_angulos_1" },
};

const InstagramStoryTemplates = ({ user, customerInfo }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  //console.log("UserInTemplate", user);
  //console.log("CustomerInTemplate", customerInfo);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await getInstagramTemplates();
        
        // Transformamos el array de strings en objetos con nombre y url
        const formattedTemplates = (data || []).map(url => {
          // Extraemos el nombre del archivo de la URL (ej: antes_despues_mock.png)
          const filename = url.split('/').pop().split('?')[0]; // Ej: "mira_mock.png"
          const filenameWithoutExt = filename.replace(/\.(png|jpg)$/, ''); // Ej: "mira_mock"

          // Buscamos la configuración en nuestro diccionario
          const config = TEMPLATE_CONFIG[filenameWithoutExt];
          
          return {
            // Usamos el displayName del diccionario, o un nombre limpio por defecto si no se encuentra
            name: config ? config.displayName : filenameWithoutExt.replace(/_/g, ' '),
            // Usamos el imageTemplateModel del diccionario, o el nombre del archivo sin extensión por defecto
            modelId: config ? config.imageTemplateModel : filenameWithoutExt,
            url: url
          };
        });
        setTemplates(formattedTemplates);
        if (formattedTemplates.length === 0) {
          setAllImagesLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("No se pudieron cargar los templates de Instagram");
        setAllImagesLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleImageLoad = () => {
    setLoadedCount((prev) => {
      const newCount = prev + 1;
      if (newCount >= templates.length) {
        setAllImagesLoaded(true);
      }
      return newCount;
    });
  };

  const handleGenerate = async (template) => {
    setGeneratingId(template.modelId);
    
    const payload = {
      client: customerInfo?.name || "Cliente Lisual",
      ftpUser: user?.feedID,
      imageTemplateModel: template.modelId,
      // Prioritize customerInfo.logo, then user.logo, then default Lisual logo
      logoUrl: customerInfo?.logo || user?.logo || "https://firebasestorage.googleapis.com/v0/b/lisual-dashboard.firebasestorage.app/o/logos%2Flogo.png?alt=media&token=f07f2d2e-1955-49f0-a74b-c2c2ffcba888"
    };

    try {
      const blob = await generateInstagramStory(payload);
      
      // Crear un link temporal para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `story-${template.modelId}-${new Date().getTime()}.png`);
      document.body.appendChild(link);
      link.click();
      
      // Limpieza
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("¡Story generada y descargada con éxito!");
    } catch (error) {
      console.error("Error generating story:", error);
      toast.error("Error al generar la imagen. Intenta nuevamente.");
    } finally {
      setGeneratingId(null);
    }
  };

  const isReady = !loading && allImagesLoaded;

  return (
    <div className="space-y-6">
      {/* Loader Principal: Visible mientras la API carga O las imágenes se descargan */}
      {!isReady && (
        <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2" />
          <p className="text-gray-500 text-sm">Preparando templates de Instagram...</p>
        </div>
      )}

      {/* Contenedor de Templates: Se renderiza siempre para iniciar la carga, pero se oculta si no está listo */}
      <div className={!isReady ? "invisible h-0 overflow-hidden" : "animate-in fade-in duration-500"}>
      <div className="border-b pb-4">
        <h3 className="text-xl font-bold text-gray-800">Instagram Stories</h3>
        <p className="text-sm text-gray-500">Genera contenido profesional de tu obra en segundos</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div 
            key={template.name} 
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow flex flex-col"
          >
            {/* Imagen con Aspect Ratio de Story (9:16) */}
            <div className="relative aspect-[9/16] bg-gray-100 group">
              {template.url ? (
                <img 
                  src={template.url} 
                  alt={template.name}
                  onLoad={handleImageLoad}
                  onError={handleImageLoad} // Contamos el error para no trabar el loader
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="text-gray-300 w-12 h-12" />
                </div>
              )}
              
              {/* Overlay sutil */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
            </div>

            {/* Cuerpo de la card */}
            <div className="p-4 flex flex-col gap-3">
              

              <button
                onClick={() => handleGenerate(template)}
                disabled={generatingId !== null}
                className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-bold transition-all ${
                  generatingId === template.modelId
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 active:scale-95 shadow-sm"
                }`}
              >
                {generatingId === template.modelId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generar y Descargar
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default InstagramStoryTemplates;
